import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkAssignExemptOverridesOptions,
  BulkAssignExemptOverridesResponse,
  BulkCreateManualTaxMappingsOptions,
  BulkCreateManualTaxMappingsResponse,
  BulkUnassignExemptOverridesOptions,
  BulkUnassignExemptOverridesResponse,
  CreateManualTaxMappingApplicationErrors,
  ListManualTaxMappingsResponse,
  ManualTaxMapping,
  ManualTaxMappingCreatedEnvelope,
  ManualTaxMappingDeletedEnvelope,
  ManualTaxMappingQuery,
  ManualTaxMappingUpdatedEnvelope,
  ManualTaxMappingsQueryBuilder,
  QueryManualTaxMappingsResponse,
  UnassignOverride,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateManualTaxMapping,
  UpdateManualTaxMappingApplicationErrors,
  bulkAssignExemptOverrides as universalBulkAssignExemptOverrides,
  bulkCreateManualTaxMappings as universalBulkCreateManualTaxMappings,
  bulkUnassignExemptOverrides as universalBulkUnassignExemptOverrides,
  createManualTaxMapping as universalCreateManualTaxMapping,
  deleteManualTaxMapping as universalDeleteManualTaxMapping,
  getManualTaxMapping as universalGetManualTaxMapping,
  listManualTaxMappings as universalListManualTaxMappings,
  queryManualTaxMappings as universalQueryManualTaxMappings,
  typedQueryManualTaxMappings as universalTypedQueryManualTaxMappings,
  updateExtendedFields as universalUpdateExtendedFields,
  updateManualTaxMapping as universalUpdateManualTaxMapping,
} from './billing-v1-manual-tax-mapping-manual-tax-mappings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createManualTaxMapping(
  httpClient: HttpClient
): CreateManualTaxMappingSignature {
  return (
    manualTaxMapping: NonNullablePaths<
      ManualTaxMapping,
      `taxGroupId` | `taxRate` | `taxRegionId`,
      2
    >
  ) =>
    universalCreateManualTaxMapping(
      manualTaxMapping,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateManualTaxMappingSignature {
  /**
   * Creates a manual tax mapping for a specific tax group and region combination.
   *
   * You must specify the tax group ID and tax region ID. The tax group and tax region
   * must already exist before creating the mapping.
   * To create multiple manual tax mappings in a single request, use [Bulk Create Manual Tax Mappings](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/manual-tax-mappings/bulk-create-manual-tax-mappings).
   * @param - Manual tax mapping to create.
   * @returns Created manual tax mapping.
   */
  (
    manualTaxMapping: NonNullablePaths<
      ManualTaxMapping,
      `taxGroupId` | `taxRate` | `taxRegionId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ManualTaxMapping,
      | `taxRate`
      | `taxType`
      | `taxName`
      | `jurisdiction`
      | `jurisdictionType`
      | `exemptRates`
      | `exemptRates.${number}.taxExemptGroupId`
      | `exemptRates.${number}.taxRate`,
      4
    > & {
      __applicationErrorsType?: CreateManualTaxMappingApplicationErrors;
    }
  >;
}

export function bulkCreateManualTaxMappings(
  httpClient: HttpClient
): BulkCreateManualTaxMappingsSignature {
  return (
    manualTaxMappings: NonNullablePaths<
      ManualTaxMapping,
      `taxGroupId` | `taxRate` | `taxRegionId`,
      2
    >[],
    options?: BulkCreateManualTaxMappingsOptions
  ) =>
    universalBulkCreateManualTaxMappings(
      manualTaxMappings,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateManualTaxMappingsSignature {
  /**
   * Creates up to 100 manual tax mappings in a single request.
   *
   * Use this method when you need to set up tax mappings for multiple tax group and region combinations at once,
   * such as when initially configuring tax rates for a new business or importing mappings from an external system.
   * @param - Manual tax mappings to create.
   */
  (
    manualTaxMappings: NonNullablePaths<
      ManualTaxMapping,
      `taxGroupId` | `taxRate` | `taxRegionId`,
      2
    >[],
    options?: BulkCreateManualTaxMappingsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateManualTaxMappingsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.taxRate`
      | `results.${number}.item.taxType`
      | `results.${number}.item.taxName`
      | `results.${number}.item.jurisdiction`
      | `results.${number}.item.jurisdictionType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getManualTaxMapping(
  httpClient: HttpClient
): GetManualTaxMappingSignature {
  return (manualTaxMappingId: string) =>
    universalGetManualTaxMapping(
      manualTaxMappingId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetManualTaxMappingSignature {
  /**
   * Retrieves a manual tax mapping by ID.
   *
   * Use this method to get details about a specific manual tax mapping, including its tax rate,
   * jurisdiction information, and associated tax group and region.
   * @param - Manual tax mapping ID.
   * @returns Retrieved manual tax mapping.
   */
  (manualTaxMappingId: string): Promise<
    NonNullablePaths<
      ManualTaxMapping,
      | `taxRate`
      | `taxType`
      | `taxName`
      | `jurisdiction`
      | `jurisdictionType`
      | `exemptRates`
      | `exemptRates.${number}.taxExemptGroupId`
      | `exemptRates.${number}.taxRate`,
      4
    >
  >;
}

export function updateManualTaxMapping(
  httpClient: HttpClient
): UpdateManualTaxMappingSignature {
  return (
    _id: string,
    manualTaxMapping: NonNullablePaths<UpdateManualTaxMapping, `revision`, 2>
  ) =>
    universalUpdateManualTaxMapping(
      _id,
      manualTaxMapping,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateManualTaxMappingSignature {
  /**
   * Updates an existing manual tax mapping.
   *
   * Use this method to modify the tax rate or other details of an existing manual tax mapping.
   * Each time the manual tax mapping is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the manual tax mapping.
   * This ensures you're working with the latest manual tax mapping and prevents unintended overwrites.
   * @param - Manual tax mapping ID.
   * @returns Updated manual tax mapping.
   */
  (
    _id: string,
    manualTaxMapping: NonNullablePaths<UpdateManualTaxMapping, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      ManualTaxMapping,
      | `taxRate`
      | `taxType`
      | `taxName`
      | `jurisdiction`
      | `jurisdictionType`
      | `exemptRates`
      | `exemptRates.${number}.taxExemptGroupId`
      | `exemptRates.${number}.taxRate`,
      4
    > & {
      __applicationErrorsType?: UpdateManualTaxMappingApplicationErrors;
    }
  >;
}

export function deleteManualTaxMapping(
  httpClient: HttpClient
): DeleteManualTaxMappingSignature {
  return (manualTaxMappingId: string) =>
    universalDeleteManualTaxMapping(
      manualTaxMappingId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteManualTaxMappingSignature {
  /**
   * Deletes a manual tax mapping.
   *
   * This permanently removes the custom tax rate mapping.
   * After deletion, no tax will be calculated for the associated tax group and region combination.
   * @param - Manual tax mapping ID.
   */
  (manualTaxMappingId: string): Promise<void>;
}

export function queryManualTaxMappings(
  httpClient: HttpClient
): QueryManualTaxMappingsSignature {
  return () =>
    universalQueryManualTaxMappings(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryManualTaxMappingsSignature {
  /**
   * Retrieves a list of manual tax mappings based on the provided query criteria.
   *
   * Use this method to find manual tax mappings by tax group, tax region, or other filter criteria.
   * You can also sort and paginate the results. This is useful for managing large numbers of tax mappings
   * or finding mappings that apply to specific regions or tax groups.
   *
   * To learn how to query manual tax mappings, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
   */
  (): ManualTaxMappingsQueryBuilder;
}

export function typedQueryManualTaxMappings(
  httpClient: HttpClient
): TypedQueryManualTaxMappingsSignature {
  return (query: ManualTaxMappingQuery) =>
    universalTypedQueryManualTaxMappings(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryManualTaxMappingsSignature {
  /** */
  (query: ManualTaxMappingQuery): Promise<
    NonNullablePaths<
      QueryManualTaxMappingsResponse,
      | `manualTaxMappings`
      | `manualTaxMappings.${number}.taxRate`
      | `manualTaxMappings.${number}.taxType`
      | `manualTaxMappings.${number}.taxName`
      | `manualTaxMappings.${number}.jurisdiction`
      | `manualTaxMappings.${number}.jurisdictionType`,
      4
    >
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a manual tax mapping.
   *
   * Use this method to modify extended field data associated with a manual tax mapping.
   * Unlike the standard update operation, this does not increment the revision number.
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `manualTaxMapping.taxRate`
      | `manualTaxMapping.taxType`
      | `manualTaxMapping.taxName`
      | `manualTaxMapping.jurisdiction`
      | `manualTaxMapping.jurisdictionType`
      | `manualTaxMapping.exemptRates`
      | `manualTaxMapping.exemptRates.${number}.taxExemptGroupId`
      | `manualTaxMapping.exemptRates.${number}.taxRate`,
      5
    >
  >;
}

export function bulkAssignExemptOverrides(
  httpClient: HttpClient
): BulkAssignExemptOverridesSignature {
  return (
    options?: NonNullablePaths<
      BulkAssignExemptOverridesOptions,
      | `assignOverrides.${number}.exemptGroupOverrides`
      | `assignOverrides.${number}.exemptGroupOverrides.${number}.exemptGroupId`
      | `assignOverrides.${number}.exemptGroupOverrides.${number}.rate`
      | `assignOverrides.${number}.manualTaxMappingId`,
      6
    >
  ) =>
    universalBulkAssignExemptOverrides(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkAssignExemptOverridesSignature {
  /**
   * Assigns tax exempt overrides to multiple manual tax mappings in a single request.
   */
  (
    options?: NonNullablePaths<
      BulkAssignExemptOverridesOptions,
      | `assignOverrides.${number}.exemptGroupOverrides`
      | `assignOverrides.${number}.exemptGroupOverrides.${number}.exemptGroupId`
      | `assignOverrides.${number}.exemptGroupOverrides.${number}.rate`
      | `assignOverrides.${number}.manualTaxMappingId`,
      6
    >
  ): Promise<
    NonNullablePaths<
      BulkAssignExemptOverridesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.taxRate`
      | `results.${number}.item.taxType`
      | `results.${number}.item.taxName`
      | `results.${number}.item.jurisdiction`
      | `results.${number}.item.jurisdictionType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUnassignExemptOverrides(
  httpClient: HttpClient
): BulkUnassignExemptOverridesSignature {
  return (
    unassignOverrides: NonNullablePaths<
      UnassignOverride,
      `manualTaxMappingId`,
      2
    >[],
    options?: BulkUnassignExemptOverridesOptions
  ) =>
    universalBulkUnassignExemptOverrides(
      unassignOverrides,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUnassignExemptOverridesSignature {
  /**
   * Unassigns tax exempt overrides to multiple manual tax mappings in a single request.
   * @param - List of unassign overrides.
   */
  (
    unassignOverrides: NonNullablePaths<
      UnassignOverride,
      `manualTaxMappingId`,
      2
    >[],
    options?: BulkUnassignExemptOverridesOptions
  ): Promise<
    NonNullablePaths<
      BulkUnassignExemptOverridesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.taxRate`
      | `results.${number}.item.taxType`
      | `results.${number}.item.taxName`
      | `results.${number}.item.jurisdiction`
      | `results.${number}.item.jurisdictionType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function listManualTaxMappings(
  httpClient: HttpClient
): ListManualTaxMappingsSignature {
  return () =>
    universalListManualTaxMappings(
      // @ts-ignore
      { httpClient }
    );
}

interface ListManualTaxMappingsSignature {
  /**
   * Retrieves up to 1000 manual tax mappings.
   * The returned manual tax mappings are sorted by the `createdDate` field in ascending order.
   */
  (): Promise<
    NonNullablePaths<
      ListManualTaxMappingsResponse,
      | `manualTaxMappings`
      | `manualTaxMappings.${number}.taxRate`
      | `manualTaxMappings.${number}.taxType`
      | `manualTaxMappings.${number}.taxName`
      | `manualTaxMappings.${number}.jurisdiction`
      | `manualTaxMappings.${number}.jurisdictionType`,
      4
    >
  >;
}

export const onManualTaxMappingCreated = /*#__PURE__*/ EventDefinition(
  'wix.billing.v1.manual_tax_mapping_created',
  true,
  (event: ManualTaxMappingCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ManualTaxMappingCreatedEnvelope>();
export const onManualTaxMappingDeleted = /*#__PURE__*/ EventDefinition(
  'wix.billing.v1.manual_tax_mapping_deleted',
  true,
  (event: ManualTaxMappingDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ManualTaxMappingDeletedEnvelope>();
export const onManualTaxMappingUpdated = /*#__PURE__*/ EventDefinition(
  'wix.billing.v1.manual_tax_mapping_updated',
  true,
  (event: ManualTaxMappingUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ManualTaxMappingUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  AssignOverride,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkAssignExemptOverridesOptions,
  BulkAssignExemptOverridesRequest,
  BulkAssignExemptOverridesResponse,
  BulkCreateManualTaxMappingsOptions,
  BulkCreateManualTaxMappingsRequest,
  BulkCreateManualTaxMappingsResponse,
  BulkExemptOverrideResult,
  BulkManualTaxMappingResult,
  BulkRestoreManualTaxMappingsRequest,
  BulkRestoreManualTaxMappingsResponse,
  BulkRestoreManualTaxMappingsResult,
  BulkUnassignExemptOverridesOptions,
  BulkUnassignExemptOverridesRequest,
  BulkUnassignExemptOverridesResponse,
  CreateManualTaxMappingRequest,
  CreateManualTaxMappingResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteManualTaxMappingRequest,
  DeleteManualTaxMappingResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExemptDetails,
  ExemptGroupIdOverride,
  ExtendedFields,
  GetManualTaxMappingRequest,
  GetManualTaxMappingResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  JurisdictionType,
  ListManualTaxMappingsRequest,
  ListManualTaxMappingsResponse,
  ManualTaxMapping,
  ManualTaxMappingCreatedEnvelope,
  ManualTaxMappingDeletedEnvelope,
  ManualTaxMappingQuerySpec,
  ManualTaxMappingUpdatedEnvelope,
  ManualTaxMappingsQueryBuilder,
  ManualTaxMappingsQueryResult,
  MessageEnvelope,
  QueryManualTaxMappingsRequest,
  QueryManualTaxMappingsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UnassignOverride,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateManualTaxMapping,
  UpdateManualTaxMappingRequest,
  UpdateManualTaxMappingResponse,
  WebhookIdentityType,
  utils,
} from './billing-v1-manual-tax-mapping-manual-tax-mappings.universal.js';
